import React, { useRef, useState } from 'react';
import Popover from './Popover';
import Input from './Input';
import { Icon } from '../icons';
import { useT } from '../../i18n';

/**
 * Select — single-select listbox styled to match the design system.
 * Built on Popover; renders a button trigger + a listbox panel.
 *
 * A search box is shown inside the panel automatically once the option list
 * grows past a handful of entries, so pickers backed by real data (categories,
 * locations, suppliers…) stay usable as the data grows. Pass `searchable` to
 * force it on (lookup-style fields) or off (short static enums).
 *
 * Props:
 *   options: Array<{ value, label, icon? }>
 *   value, onChange(value)
 *   placeholder: string
 *   size: 'sm' | 'md'
 *   disabled: boolean
 *   ariaLabel: string (when there is no visible <label>)
 *   matchTriggerWidth: boolean (default true)
 *   searchable: true | false | 'auto' (default 'auto': search shows at 8+ options)
 *   searchPlaceholder: string
 */
const SEARCH_THRESHOLD = 8;

const OptionList = ({ options, value, onChange, close, listRef, autoFocusList, className = '' }) => {
  const opts = () => Array.from(listRef.current?.querySelectorAll('[role="option"]') || []);
  const onKeyDown = (e) => {
    const nodes = opts();
    if (!nodes.length) return;
    const idx = nodes.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); nodes[(idx + 1) % nodes.length].focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); nodes[(idx - 1 + nodes.length) % nodes.length].focus(); }
    else if (e.key === 'Home') { e.preventDefault(); nodes[0].focus(); }
    else if (e.key === 'End') { e.preventDefault(); nodes[nodes.length - 1].focus(); }
  };

  return (
    <div className={`ps-listbox ${className}`.trim()} ref={listRef} onKeyDown={onKeyDown}>
      {options.map((opt, i) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="option"
            aria-selected={selected}
            tabIndex={-1}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocusList && (selected || (value == null && i === 0))}
            className={`ps-listbox__option ${selected ? 'is-selected' : ''}`.trim()}
            onClick={() => { onChange?.(opt.value); close?.(); }}
          >
            {opt.icon && <span className="ps-listbox__icon">{opt.icon}</span>}
            <span className="ps-listbox__label">{opt.label}</span>
            {selected && <Icon.Check size={16} className="ps-listbox__check" />}
          </button>
        );
      })}
    </div>
  );
};

const Select = ({
  options = [],
  value,
  onChange,
  placeholder,
  size = 'md',
  disabled = false,
  ariaLabel,
  matchTriggerWidth = true,
  searchable = 'auto',
  searchPlaceholder,
  className = '',
}) => {
  const t = useT();
  const [query, setQuery] = useState('');
  const listRef = useRef(null);
  const selected = options.find((o) => o.value === value);

  const showSearch = searchable === true || (searchable === 'auto' && options.length >= SEARCH_THRESHOLD);

  const filtered = showSearch && query
    ? options.filter((o) => String(o.label).toLowerCase().includes(query.toLowerCase()))
    : options;

  // From the search box, arrows jump into the option list.
  const moveFocus = (e) => {
    const nodes = Array.from(listRef.current?.querySelectorAll('[role="option"]') || []);
    if (!nodes.length) return;
    const idx = nodes.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); (nodes[idx + 1] || nodes[0]).focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); (nodes[idx - 1] || nodes[nodes.length - 1]).focus(); }
    else if (e.key === 'Enter' && document.activeElement?.getAttribute('role') !== 'option' && nodes.length === 1) {
      // Enter with a single match selects it.
      e.preventDefault();
      nodes[0].click();
    }
  };

  const trigger = (
    <button
      type="button"
      className={`ps-select ps-select--${size} ${disabled ? 'is-disabled' : ''}`.trim()}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      <span className={`ps-select__value ${selected ? '' : 'ps-select__value--placeholder'}`.trim()}>
        {selected ? selected.label : (placeholder ?? t('Select…'))}
      </span>
      <Icon.ChevronsUpDown size={16} className="ps-select__caret" />
    </button>
  );

  return (
    <Popover
      trigger={trigger}
      role={showSearch ? 'dialog' : 'listbox'}
      placement="bottom-start"
      matchTriggerWidth={matchTriggerWidth}
      className={className}
      panelClassName={showSearch ? 'ps-popover__panel--combobox' : 'ps-popover__panel--listbox'}
      onOpenChange={(open) => { if (open) setQuery(''); }}
    >
      {({ close }) => (
        showSearch ? (
          <div className="ps-combobox">
            <div className="ps-combobox__search">
              <Input
                size="sm"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                leadingIcon={<Icon.Search />}
                placeholder={searchPlaceholder ?? t('Search…')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={moveFocus}
              />
            </div>
            {filtered.length === 0 ? (
              <p className="ps-combobox__empty">{t('No matches')}</p>
            ) : (
              <OptionList
                className="ps-combobox__list"
                options={filtered}
                value={value}
                onChange={(v) => { onChange?.(v); setQuery(''); }}
                close={close}
                listRef={listRef}
                autoFocusList={false}
              />
            )}
          </div>
        ) : (
          <OptionList
            options={filtered}
            value={value}
            onChange={onChange}
            close={close}
            listRef={listRef}
            autoFocusList
          />
        )
      )}
    </Popover>
  );
};

export default Select;
